/**
 * @file poUnitsFromBill.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-22
 * @modified 2026-10-22
 * @description A PO line put in its bill's units (Cam, 22 Oct 2026 — "keep the
 *              bill's units and change our PO's to match the supplier's, and
 *              again do the maths on the unit cost for them").
 *
 *   THREE WAYS when a bill line and its PO line are counted differently
 *   (utils/billUnitConversion decides that, on evidence):
 *     1. the BILL takes the PO's units — utils/billLineChoices.convertToPoUnits
 *        (the bill line stored as the PO counts it, its amount as invoiced);
 *     2. the PO takes the BILL's units — this file: the PO line's quantity,
 *        cost, unit weight, qty per pallet and supplied quantity converted, its
 *        line amount and pallets unchanged — and its order line likewise
 *        (quantity, sale price, cost price, supplied / delivered quantity,
 *        unit weight, qty per pallet; line total and pallets unchanged);
 *     3. both kept as they are — the cost write-back already compares in the
 *        PO line's unit ("Also set the PO's cost to £10.00 — £400.00 a tonne
 *        = £10.00 a 25kg bag").
 *
 *   THE MATHS. The conversion's ratio r turns a bill quantity into the PO
 *   line's (× r). The PO line in the bill's unit: quantity ÷ r, cost × r — 40 ×
 *   25kg bag at £10.00 is 1.00 T at £400.00 (r = 40); 1.20 T at £631.00 is
 *   48 × 25kg bag at £15.775 (r = 1/40). The cost is the one at 4 decimal
 *   places that keeps the line's amount to the penny (the nearest, else one
 *   either side); when none does it is not offered, with the figures. The unit
 *   weight becomes 1000 kg for tonnes, the pack's for packs.
 *
 *   A PO COSTED IN THE BILL'S UNIT ALREADY (the conversion was decided on the
 *   quantity because the price could not — `costIgnored` — and the PO's cost
 *   is within 25% of the bill's price as billed): NWF bill 44192, 1.00 T at
 *   £380 against PO-D10453 2 × 500kg tote at £380 — the tote was costed at
 *   the tonne's price. Put in tonnes, the PO line keeps its cost (£380 a
 *   tonne) and its amount follows the quantity (1.00 T × £380 = £380, the
 *   bill's) — `costKept`; its order line's cost price is kept with it.
 *
 *   NOT OFFERED (the reason is said, never hidden): a bill in KILOGRAMS (a PO
 *   line is counted in packs or tonnes, never kg); a PO line with additives
 *   (they are priced per unit — change the units on the order); the order
 *   line with an additive price of its own (the same). The callers add the
 *   rest — the bill in Xero, a PO made in Xero while the Xero PO sync is on
 *   (nightly / off its new quantity and cost go to Xero with the bill save,
 *   2026-10-22), a PO-H, several bill lines,
 *   a split family's shared line, a PO billed by another bill, an order with
 *   an invoice (CreateBill and billService._applyAddBillPoEdits).
 *
 *   Pure. The server's utils/poUnitsFromBill.js is GENERATED from this file by
 *   scripts/sync-shared-utils.js — edit this one and re-run it.
 */

import { roundHalfUp } from './numberFormatters';
import { quantityInUnit, money } from './billLineChoices';

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = parseFloat(String(v).replace(/[£,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};
const r2 = (n) => roundHalfUp(n, 2);
const r4 = (n) => roundHalfUp(n, 4);
const EPS = 0.0001;
const pounds = (v) => `£${(r2(num(v) ?? 0) ?? 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const no = (reason) => ({ ok: false, reason });

/**
 * The price at 4 dp, per a quantity in another unit, that keeps `amount` to
 * the penny: the nearest to price × ratio, else one either side; null if none.
 */
export function priceKeepingAmount({ quantity, price, ratio, amount }) {
  const q = num(quantity);
  const p = num(price);
  if (!(q > 0) || p === null) return null;
  if (p === 0) return 0;
  const near = r4(p * ratio);
  const target = r2(amount);
  const found = [near, r4(near - EPS), r4(near + EPS)].find(c => c > 0 && r2(q * c) === target);
  return found === undefined ? null : found;
}

/** What the PO line's additives add to its cost (one or more priced additives). */
function additivesPriced(li) {
  return (Array.isArray(li?.additives) ? li.additives : [])
    .some(a => (num(a?.additivePrice) ?? num(a?.additive_price) ?? 0) > 0);
}

/**
 * The PO line in the bill's units.
 *
 * @param {object} p
 * @param {object|null} p.conv — utils/billUnitConversion for this PO line's bill lines
 * @param {object} p.poLine — the PO's line_items element (quantity, unitAmount, lineAmount,
 *        unitWeightKg, qtyPerPallet, pallets, additives)
 * @param {number|null} [p.supplied] — what was supplied on it, where recorded
 * @param {string} [p.poNumber]
 * @returns {{ ok: true, ratio: number, toUnit: 't'|'pack', toKg: number, fromUnit: 't'|'pack', fromKg: number,
 *   quantity: number, unitAmount: number, amount: number, costKept: boolean, amountBefore: number,
 *   supplied: number|null, qtyPerPallet: number|null,
 *   before: { quantity: number, unitAmount: number, unitWeightKg: any, qtyPerPallet: any, supplied: number|null },
 *   text: string } | { ok: false, reason: string }}
 */
export function poUnitsFromBill({ conv, poLine, supplied = null, poNumber = 'The PO' } = {}) {
  if (!conv) return no('The bill and the PO line read as one unit — nothing to change.');
  if (!poLine) return no('This line is not on a line of the PO.');
  if (conv.billUnit === 'kg') {
    return no(`Billed per kg — ${poNumber} counts in ${conv.poUnit === 'tonnes' ? 'tonnes' : 'packs'}, and a PO line is not kept in kilograms. Put the bill in the PO's units, or keep both.`);
  }
  if (additivesPriced(poLine)) {
    return no(`${poNumber}'s line has additives priced per ${conv.poUnit === 'tonnes' ? 'tonne' : 'pack'} — change its units on the customer order, or keep both.`);
  }
  const r = num(conv.ratio);
  const q = num(poLine.quantity);
  const c = num(poLine.unitAmount) ?? 0;
  if (!(r > 0) || !(q > 0)) return no(`${poNumber}'s line has no quantity to convert.`);
  const quantity = r4(q / r);
  if (!(quantity > 0)) return no(`${poNumber}'s quantity comes to nothing in the bill's units.`);
  // Already costed in the bill's unit: the cost stays, the amount follows.
  const bc = num(conv.billCost);
  const costKept = !!conv.costIgnored && c > 0 && bc > 0 && Math.abs(bc / c - 1) <= 0.25 + EPS;
  const before = r2(q * c);
  const amount = costKept ? r2(quantity * c) : before;
  const unitAmount = costKept ? r4(c) : priceKeepingAmount({ quantity, price: c, ratio: r, amount });
  const toUnit = conv.billUnit === 'tonnes' ? 't' : 'pack';
  const fromUnit = conv.poUnit === 'tonnes' ? 't' : 'pack';
  const kg = num(conv.packKg ?? conv.kg);
  const toKg = toUnit === 't' ? 1000 : kg;
  const fromKg = fromUnit === 't' ? 1000 : kg;
  if (unitAmount === null) {
    return no(`${quantityInUnit(quantity, toUnit, toKg)} at ${money(r4(c * r))} comes to ${pounds(r2(quantity * r4(c * r)))}, not the `
      + `${pounds(before)} ${poNumber}'s line comes to — so its units stay as they are.`);
  }
  const s = num(supplied);
  const ppl = num(poLine.qtyPerPallet);
  const from = `${quantityInUnit(q, fromUnit, fromKg)} at ${money(c)}`;
  const to = `${quantityInUnit(quantity, toUnit, toKg)} at ${money(unitAmount)}`;
  const per = toUnit === 't' ? 'tonne' : quantityInUnit(1, 'pack', toKg).replace(/^1 × /, '');
  const money2 = costKept
    ? ` — its cost was already per ${per}, so it stays ${money(unitAmount)} and the line comes to ${pounds(amount)} (was ${pounds(before)})`
    : ` (same ${pounds(amount)})`;
  return {
    ok: true, ratio: r, toUnit, toKg, fromUnit, fromKg,
    quantity, unitAmount, amount, costKept, amountBefore: before,
    supplied: s === null ? null : r4(s / r),
    qtyPerPallet: ppl > 0 ? r4(ppl / r) : null,
    before: { quantity: r4(q), unitAmount: r4(c), unitWeightKg: poLine.unitWeightKg ?? null, qtyPerPallet: poLine.qtyPerPallet ?? null, supplied: s },
    text: `${poNumber}: ${from} → ${to}${money2}${s !== null ? `; supplied ${quantityInUnit(s, fromUnit, fromKg)} → ${quantityInUnit(r4(s / r), toUnit, toKg)}` : ''}`,
  };
}

/**
 * The PO line as it will be (a copy): quantity, cost, unit weight and qty per
 * pallet from `take`; its line amount and tax unchanged; a stock line's own
 * supplied quantity converted.
 */
export function relabelPoLine(li, take) {
  if (!li || !take?.ok) return li;
  const next = { ...li, quantity: take.quantity, unitAmount: take.unitAmount, unitWeightKg: take.toKg };
  if (take.costKept) {
    // The amount follows the quantity, the tax at the line's own rate.
    const oldNet = num(li.lineAmount);
    const rate = oldNet && Math.abs(oldNet) >= 0.01 && num(li.taxAmount) !== null ? num(li.taxAmount) / oldNet : 0;
    next.lineAmount = take.amount;
    next.taxAmount = r2(take.amount * rate);
  }
  if (take.qtyPerPallet !== null) next.qtyPerPallet = take.qtyPerPallet;
  const own = num(li.suppliedQuantity);
  if (own !== null) next.suppliedQuantity = r4(own / take.ratio);
  return next;
}

/**
 * Its order line in the same units (the fields to write): quantity, the sale
 * price keeping the line total, the cost price, supplied / delivered
 * quantities and qty per pallet ÷ / × the ratio; unit weight as the PO line's;
 * pallets as they are. Where the PO line kept its cost (`costKept` — it was in
 * the bill's unit already), the order line's cost price is kept too. Null with a reason when it cannot follow.
 * @param {object} ol — the order line (quantity, unit_price, line_total, cost_price,
 *        supplied_quantity, delivered_quantity, qty_per_pallet, additive_price)
 * @param {ReturnType<typeof poUnitsFromBill>} take — ok
 * @returns {{ ok: true, fields: object, changes: object } | { ok: false, reason: string }}
 */
export function relabelOrderLine(ol, take, { orderNumber = 'The order' } = {}) {
  if (!ol || !take?.ok) return no('Nothing to change.');
  if ((num(ol.additive_price) ?? 0) > 0) {
    return no(`${orderNumber}'s line has an additive priced per unit — change its units on the order, or keep both.`);
  }
  const r = take.ratio;
  const q = num(ol.quantity) ?? 0;
  const quantity = r4(q / r);
  const price = num(ol.unit_price) ?? 0;
  const total = num(ol.line_total);
  const keepTotal = total !== null ? r2(total) : r2(q * price);
  const unitPrice = q > 0 ? priceKeepingAmount({ quantity, price, ratio: r, amount: keepTotal }) : r4(price * r);
  if (unitPrice === null) {
    return no(`${orderNumber}'s line would not keep its total (${pounds(keepTotal)}) in the bill's units — so its units stay as they are.`);
  }
  const scale = (v, by) => (num(v) === null ? null : r4(num(v) * by));
  const fields = {
    quantity,
    unit_price: unitPrice,
    cost_price: take.costKept ? (num(ol.cost_price) === null ? null : r4(num(ol.cost_price))) : scale(ol.cost_price, r),
    supplied_quantity: scale(ol.supplied_quantity, 1 / r),
    delivered_quantity: scale(ol.delivered_quantity, 1 / r),
    qty_per_pallet: num(ol.qty_per_pallet) > 0 ? r4(num(ol.qty_per_pallet) / r) : (num(ol.qty_per_pallet) === null ? null : num(ol.qty_per_pallet)),
    unit_weight_kg: take.toKg,
  };
  const changes = {};
  for (const [f, v] of Object.entries(fields)) {
    const was = num(ol[f]);
    const now = num(v);
    if ((was === null) !== (now === null) || (was !== null && r4(was) !== r4(now))) changes[f] = { old: was === null ? null : r4(was), new: now === null ? null : r4(now) };
  }
  return { ok: true, fields, changes };
}

/** "1.00 T at £400.00" — the PO line in the bill's units, for the Quantity cell. */
export function poUnitsShortText(take) {
  if (!take?.ok) return '';
  return `${quantityInUnit(take.quantity, take.toUnit, take.toKg)} at ${money(take.unitAmount)}`;
}
